import { DestroyRef, Injectable, inject } from '@angular/core';
import { Observable, filter, tap } from 'rxjs';
import { BroadcastPeriod } from '~core/models/core.models';
import { DeleteConfirmComponent } from '~backoffice/app/~shared/components/modals/delete-confirm/delete-confirm.component';
import { SnackbarService } from './snackbar.service';
import { ResourceFacade } from '~backoffice/app/~store/facades/resource.facade';
import { ProgramService } from '~core/services/program.service';
import { EditProgramComponent } from '../../program/modals/edit-program/edit-program.component';
import { ShareComponent } from '~backoffice/app/~shared/components/share/share.component';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { ProgramBannerEditorComponent } from '../../program/modals/program-banner/program-banner-editor/program-banner-editor.component';
import { ProgramScheduleComponent } from '../../program/modals/program-schedule/program-schedule.component';
import { isEqual } from 'lodash-es';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Program } from '~core/models/program.models';
import { DialogService } from '~core/services/dialog.service';
import { EditProgramBannerComponent } from '~backoffice/app/program/modals/edit-program-banner/edit-program-banner.component';
import { EditProgramTeaserComponent } from '~backoffice/app/program/modals/edit-program-teaser/edit-program-teaser.component';

@Injectable()
export class ProgramManagementService {
  private resourceFacade = inject(ResourceFacade);
  private programService = inject(ProgramService);
  private globalFacade = inject(GlobalFacade);
  // private programBannerFacade = inject(ProgramBannerFacade);
  private dialogService = inject(DialogService);
  private snackBar = inject(SnackbarService);
  protected _destroyRef = inject(DestroyRef);

  edit(program?: Program): Observable<unknown> {
    this.globalFacade.setFormViolations([]);
    const dialogRef = this.dialogService.open({
      component: EditProgramComponent,
      config: {
        panelClass: ['mat-dialog-custom-full-screen'],
        disableClose: true,
        height: '100vh',
        width: '100vw',
        maxWidth: '100vw'
      }
    });

    dialogRef.componentInstance.program = program;
    return dialogRef.afterClosed();
  }

  editBanner(program: Program): Observable<unknown> {
    this.globalFacade.setFormViolations([]);
    const dialogRef = this.dialogService.open({
      component: EditProgramBannerComponent,
      config: {
        panelClass: ['mat-dialog-custom-full-screen'],
        disableClose: true,
        height: '100vh',
        width: '100vw',
        maxWidth: '100vw'
      }
    });

    dialogRef.componentInstance.program = program;
    return dialogRef.afterClosed().pipe(
      tap({
        next: res => {
          if (res) {
            this.resourceFacade.patchItem({
              item: program,
              newData: res
            });
          }
        }
      })
    );
  }

  editTeaser(program: Program): Observable<unknown> {
    this.globalFacade.setFormViolations([]);
    const dialogRef = this.dialogService.open({
      component: EditProgramTeaserComponent,
      config: {
        panelClass: ['mat-dialog-custom-full-screen'],
        disableClose: true,
        height: '100vh',
        width: '100vw',
        maxWidth: '100vw'
      }
    });

    dialogRef.componentInstance.program = program;
    return dialogRef.afterClosed().pipe(
      tap({
        next: res => {
          if (res) {
            this.resourceFacade.patchItem({
              item: program,
              newData: res
            });
          }
        }
      })
    );
  }

  editBanners(program: Program): Observable<unknown> {
    this.globalFacade.setFormViolations([]);
    const dialogRef = this.dialogService.open({
      component: ProgramBannerEditorComponent,
      config: {
        panelClass: ['mat-dialog-file-upload'],
        disableClose: true,
        maxWidth: '80vw'
      }
    });

    dialogRef.componentInstance.program = program;
    return dialogRef.afterClosed().pipe(
      tap({
        next: () => {
          // const banners = this.programBannerFacade.getCreatedBanners() || [];
          // this.resourceFacade.patchItem({
          //   item: program,
          //   newData: {
          //     banners: uniqBy(
          //       [
          //         ...program.banners?.filter(
          //           el => el.metadata?.type !== AttachmentMetadataType.banner
          //         ),
          //         ...banners
          //       ],
          //       'id'
          //     )
          //   }
          // });
        }
      })
    );
  }

  editSchedule(program: Program): Observable<unknown> {
    this.globalFacade.setFormViolations([]);
    const dialogRef = this.dialogService.open({
      component: ProgramScheduleComponent,
      config: {
        panelClass: ['mat-dialog-custom-full-screen'],
        disableClose: true,
        height: '100vh',
        width: '100vw',
        maxWidth: '100vw'
      }
    });

    dialogRef.componentInstance.program = program;
    return dialogRef.afterClosed().pipe(
      tap({
        next: () => {
          const broadcastPeriods =
            dialogRef.componentInstance.form?.value.broadcastPeriods.filter(
              (el: BroadcastPeriod) => !!el.id
            ) as BroadcastPeriod[] | undefined;
          const areValuesSame = isEqual(
            broadcastPeriods?.map(el => el.value),
            program.broadcastPeriods?.map(el => el.value)
          );
          if (!areValuesSame) {
            this.resourceFacade.patchItem({
              item: program,
              newData: {
                broadcastPeriods: broadcastPeriods || []
              }
            });
          }
        }
      })
    );
  }

  share(program: Program): Observable<unknown> {
    const dialogRef = this.dialogService.open({
      component: ShareComponent,
      config: { panelClass: ['mat-dialog-custom'], disableClose: true }
    });

    dialogRef.componentInstance.program = program;
    return dialogRef.afterClosed();
  }

  delete(program: Program): void {
    const delete$ = this.programService.delete(program.id);
    const dialogRef = this.dialogService.open({
      component: DeleteConfirmComponent,
      config: {
        panelClass: ['mat-dialog-custom'],
        disableClose: true
      }
    });
    dialogRef.componentRef!.setInput('removeObservable$', delete$);
    dialogRef.componentRef?.setInput(
      'title',
      'Voulez-vous supprimer ce programme ?'
    );
    dialogRef
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(Boolean),
        tap({
          next: () => {
            this.resourceFacade.getData({ refresh: true });
            this.snackBar.open({
              text: 'Programme supprimé avec succès',
              type: 'success'
            });
          }
        })
      )
      .subscribe();
  }

  publish(program: Program): Observable<Program> {
    const newData = {
      published: true
    };
    return this.programService.update(program.id, newData).pipe(
      tap({
        next: () => {
          this.resourceFacade.patchItem({
            item: program,
            newData
          });
          this.snackBar.open({
            text: 'Statut de publication mis à jour',
            type: 'success'
          });
        }
      })
    );
  }

  unpublish(program: Program): Observable<unknown> {
    const delete$ = this.programService.update(program.id, {
      published: false
    });
    const dialogRef = this.dialogService.open({
      component: DeleteConfirmComponent,
      config: {
        panelClass: ['mat-dialog-custom'],
        disableClose: true
      }
    });

    dialogRef.componentRef?.setInput('removeObservable$', delete$);
    dialogRef.componentRef?.setInput(
      'title',
      'Voulez-vous dépublier cette émission ?'
    );
    dialogRef.componentRef?.setInput(
      'instruction',
      `Veuillez saisir le texte ci-dessous pour confirmer la dépublication`
    );
    dialogRef.componentRef?.setInput(
      'wordEntry',
      'Je confirme la dépublication'
    );
    dialogRef.componentRef?.setInput('action', 'Dépublier');

    return dialogRef.afterClosed().pipe(
      filter(Boolean),
      tap({
        next: () => {
          this.resourceFacade.patchItem({
            item: program,
            newData: {
              published: false
            }
          });
          this.snackBar.open({
            text: 'Programme dépublié',
            type: 'success'
          });
        }
      })
    );
  }

  togglePublishedState(
    program: Program,
    oldState?: boolean
  ): Observable<unknown> {
    return oldState ? this.unpublish(program) : this.publish(program);
  }
}
